/* * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights
Reserved. * * Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with * the License. A copy of the
License is located at * * http://aws.amazon.com/apache2.0/ * * or in the
"license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR * CONDITIONS OF ANY KIND, either express or
implied. See the License for the specific language governing permissions * and
limitations under the License. */

<template>
  <ValidationObserver ref="obs" v-slot="{ invalid, validated }">
    <v-card
      max-width="375"
      color="transparent"
      :elevation="0"
      v-bind:data-test="auth.signIn.section"
    >
      <v-card-title v-bind:data-test="auth.signIn.headerSection">
        {{ options.header }}
      </v-card-title>
      <v-alert
        v-if="error"
        v-model="alert"
        v-bind:data-test="auth.signIn.signInError"
        prominent
        dense
        dismissible
        type="error"
        transition="scale-transition"
      >
        <v-layout align-center wrap>
          <div>
            {{ error }}
          </div>
        </v-layout>
      </v-alert>
      <v-card-text v-bind:data-test="auth.signIn.bodySection">
        <form>
          <amplify-username-field
            v-bind:usernameAttributes="usernameAttributes"
            v-on:username-field-changed="usernameFieldChanged"
          >
          </amplify-username-field>

          <ValidationProvider
            :name="`${$Amplify.I18n.get('Password')}`"
            rules="required"
            v-slot="{ errors, valid }"
          >
            <v-text-field
              prepend-icon="mdi-key"
              v-model="password"
              :placeholder="$Amplify.I18n.get('Enter your password')"
              :label="`${$Amplify.I18n.get('Password')} *`"
              :append-icon="passwordVisible ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="passwordVisible = !passwordVisible"
              :type="passwordVisible ? 'text' : 'password'"
              v-on:keyup.enter="signIn"
              v-bind:data-test="auth.signIn.passwordInput"
              :error-messages="errors"
              :success="valid"
            ></v-text-field>
          </ValidationProvider>

          <div>
            {{ $Amplify.I18n.get("Forget your password?") }}
            <a
              v-on:click="forgot"
              v-bind:data-test="auth.signIn.forgotPasswordLink"
            >
              {{ $Amplify.I18n.get("Reset password") }}</a
            >
          </div>
        </form>
      </v-card-text>
      <v-card-actions v-bind:data-test="auth.signIn.footerSection">
        <v-container fluid ma-0 pa-0>
          <v-layout wrap>
           <v-flex pa-2 order-xl1 order-lg1 order-md1 order-sm1 order-xs2>
              <v-btn
                block
                v-if="options.isSignUpDisplayed"
                v-on:click="signUp"
                outlined
                color="secondary"
                v-bind:data-test="auth.signIn.createAccountLink"
                >{{ $Amplify.I18n.get("Create account") }}</v-btn
              >
            </v-flex> 

            <v-flex pa-2 order-xl2 order-lg2 order-md2 order-sm2 order-xs1>
              <v-btn
                block
                v-on:click="signIn"
                v-bind:data-test="auth.signIn.signInButton"
                color="primary"
                :disabled="invalid || !validated || loadingBtn"
                :loading="loadingBtn"
              >
                {{ $Amplify.I18n.get("Sign In") }}
                <template v-slot:loader>
                  <v-progress-circular
                    :size="18"
                    :width="2"
                    indeterminate
                    class="mr-1"
                  ></v-progress-circular>
                  <span>{{ $Amplify.I18n.get("Wait...") }}</span>
                </template>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-actions>

      <v-card-actions>
        <span class="text-center body-2 ">
          {{ $Amplify.I18n.get("The use of this service is subject to our") }}
          <a> {{ $Amplify.I18n.get("Terms of use") }} </a>
          {{ $Amplify.I18n.get("and") }}
          <a>{{ $Amplify.I18n.get("Privacy policy") }}</a>
        </span>
        <v-spacer></v-spacer>
        <v-btn small icon @click="more = !more">
          <v-icon>{{ more ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </v-btn>
      </v-card-actions>

      <v-slide-y-transition>
        <v-card-text v-show="more">
          <p v-show="more" class="text-center">
            {{
              $Amplify.I18n.get(
                "PacReport integrates with third-party identity services for the authentication and authorization of users with data encryption both in transit and at rest and that meets the requirements of HIPAA and PCI DSS, SOC, ISO / EIC 27001, ISO / EIC 27017, ISO / EIC 27018 and ISO 9001."
              )
            }}
          </p>
        </v-card-text>
      </v-slide-y-transition>
    </v-card>
  </ValidationObserver>
</template>

<script>
// import Auth from '@aws-amplify/auth';
import { AmplifyEventBus } from "aws-amplify-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";

import Vue from "vue";
import UsernameField from "./UsernameField";

Vue.component("amplify-username-field", UsernameField);
import { auth } from "aws-amplify-vue/src/assets/data-test-attributes";

export default {
  name: "SignIn",
  components: {
    ValidationProvider,
    ValidationObserver
  },
  props: ["signInConfig", "usernameAttributes"],
  data() {
    return {
      password: "",
      passwordVisible: false,
      error: "",
      auth,
      logger: {},
      signInUsername: "",
      labelMap: {
        email: "Email",
        phone_number: "Phone Number",
        username: "Username"
      },
      alert: false,
      loadingBtn: false,
      more: false
    };
  },
  computed: {
    options() {
      const defaults = {
        header: this.$Amplify.I18n.get("Sign in to your account"),
        username: "",
        isSignUpDisplayed: true
      };
      return Object.assign(defaults, this.signInConfig || {});
    }
  },
  mounted() {
    this.logger = new this.$Amplify.Logger(this.$options.name);
  },
  methods: {
    signIn: function() {
      this.loadingBtn = true;
      this.$Amplify.Auth.signIn(this.signInUsername, this.password)
        .then(data => {
          this.loadingBtn = false;
          this.logger.info("sign in success");
          if (
            data.challengeName === "SMS_MFA" ||
            data.challengeName === "SOFTWARE_TOKEN_MFA"
          ) {
            AmplifyEventBus.$emit("localUser", data);
            return AmplifyEventBus.$emit("authState", "confirmSignIn");
          } else if (data.challengeName === "NEW_PASSWORD_REQUIRED") {
            AmplifyEventBus.$emit("localUser", data);
            return AmplifyEventBus.$emit("authState", "requireNewPassword");
          } else if (data.challengeName === "MFA_SETUP") {
            AmplifyEventBus.$emit("localUser", data);
            return AmplifyEventBus.$emit("authState", "setMfa");
          } else if (
            data.challengeName === "CUSTOM_CHALLENGE" &&
            data.challengeParam &&
            data.challengeParam.trigger === "true"
          ) {
            AmplifyEventBus.$emit("localUser", data);
            return AmplifyEventBus.$emit("authState", "customConfirmSignIn");
          } else {
            return AmplifyEventBus.$emit("authState", "signedIn");
          }
        })
        .catch(e => {
          this.loadingBtn = false;
          if (e.code && e.code === "UserNotConfirmedException") {
            AmplifyEventBus.$emit("localUser", {
              username: this.signInUsername
            });
            AmplifyEventBus.$emit("authState", "confirmSignUp");
          } else {
            this.setError(e);
          }
        });
    },
    forgot: function() {
      AmplifyEventBus.$emit("authState", "forgotPassword");
    },
    signUp: function() {
      AmplifyEventBus.$emit("authState", "signUp");
    },
    setError: function(e) {
      this.alert = true;
      this.error = this.$Amplify.I18n.get(e.message || e);
      this.logger.error(this.error);
    },

    usernameFieldChanged: function(data) {
      const { usernameField, username, email, phoneNumber } = data;
      switch (usernameField) {
        case "username":
          this.signInUsername = username;
          break;
        case "email":
          this.signInUsername = email;
          break;
        case "phone_number":
          this.signInUsername = phoneNumber;
          break;
        default:
          break;
      }
    }
  }
};
</script>
